import { Fragment, useEffect, useState } from "react";
import {
  Phone,
  Smartphone,
  FileText,
  Home,
  CalendarDays,
  RotateCw,
  Webhook,
  Users,
  LogOut,
  Building2,
  ListOrdered,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged, type User } from "firebase/auth";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { ENVIRONMENT } from "@/config";
import { useOpenDayCalls } from "@/components/day-calls-drawer";
import { useWebhookFailureCount } from "@/hooks/use-webhook-failures";
import { auth, logOut } from "@/lib/firebase";

const NAV_ITEMS = [
  { title: "Dashboard", path: "/", icon: Home },
  { title: "PBM directory", path: "/payer-directory", icon: Building2 },
  { title: "Daily Intake", path: "/intake", icon: CalendarDays },
  { title: "Retry Console", path: "/retry", icon: RotateCw },
  { title: "Patient Registry", path: "/patients", icon: Users },
  { title: "Last Calls", path: "/calls", icon: Phone },
  { title: "Phone numbers", path: "/phone-numbers", icon: Smartphone },
  { title: "Case Records", path: "/cases", icon: FileText },
];

const WEBHOOK_FAILURES_NAV = {
  title: "Webhook failures",
  path: "/webhook-failures",
  icon: Webhook,
} as const;

const ENV_COLORS: Record<string, string> = {
  DEVELOPMENT: "bg-blue-100 text-blue-800",
  TEST: "bg-yellow-100 text-yellow-800",
  PRODUCTION: "bg-red-100 text-red-800",
};

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const openDayCalls = useOpenDayCalls();
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const { total: webhookFailureTotal } = useWebhookFailureCount();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, setUser);
    return () => unsub();
  }, []);

  const initials = (user?.displayName || user?.email || "?")
    .split(/[ @]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <Sidebar>
      <SidebarHeader className="border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold tracking-tight">Redi</span>
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              ENV_COLORS[ENVIRONMENT] || ENV_COLORS.DEVELOPMENT
            }`}
          >
            {ENVIRONMENT}
          </span>
        </div>
      </SidebarHeader>
      <SidebarContent className="flex flex-col">
        <SidebarGroup className="flex-1">
          <SidebarGroupLabel>Dashboard</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV_ITEMS.map((item) => (
                <Fragment key={item.path}>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={
                        item.path === "/"
                          ? location.pathname === "/"
                          : location.pathname.startsWith(item.path)
                      }
                      onClick={() => navigate(item.path)}
                      tooltip={item.title}
                    >
                      <item.icon className="h-4 w-4" />
                      <span className="flex-1">{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {item.path === "/" ? (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => openDayCalls()}
                        tooltip="Day's calls"
                      >
                        <ListOrdered className="h-4 w-4" />
                        <span className="flex-1">Day's calls</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ) : null}
                </Fragment>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="mt-auto border-t pt-2">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  size="sm"
                  className="text-muted-foreground"
                  isActive={location.pathname.startsWith(WEBHOOK_FAILURES_NAV.path)}
                  onClick={() => navigate(WEBHOOK_FAILURES_NAV.path)}
                  tooltip={WEBHOOK_FAILURES_NAV.title}
                >
                  <WEBHOOK_FAILURES_NAV.icon className="h-3.5 w-3.5 opacity-70" />
                  <span className="flex-1 text-xs font-normal">
                    {WEBHOOK_FAILURES_NAV.title}
                  </span>
                  {webhookFailureTotal > 0 ? (
                    <span className="ml-auto min-w-[1.125rem] rounded-full bg-destructive px-1 py-px text-center text-[9px] font-semibold leading-none text-destructive-foreground">
                      {webhookFailureTotal > 99 ? "99+" : webhookFailureTotal}
                    </span>
                  ) : null}
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      {user && (
        <SidebarFooter className="border-t p-2">
          <div className="flex items-center gap-2 px-2 py-1.5">
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt=""
                className="h-7 w-7 rounded-full border"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="h-7 w-7 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-[11px] font-medium">
                {initials}
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate text-xs font-medium">
                {user.displayName || user.email}
              </span>
              {user.displayName && (
                <span className="truncate text-[11px] text-muted-foreground">
                  {user.email}
                </span>
              )}
            </div>
            <button
              onClick={() => logOut().catch(() => {})}
              title="Sign out"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </SidebarFooter>
      )}
    </Sidebar>
  );
}
